import './rater.content.table.scss';

import {
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  styled,
} from '@mui/material';
import {
  RaterCoverageList,
  RaterEndorsementList,
  RaterSubjectivitiesList,
} from './coverage-rating';
import {
  useRaterActionDispatchContext,
  useRaterStateContextSelector,
} from './data';

import { ConvergeTooltip } from '../../../presentation';
import { EXPANDED_CELL_SX } from './coverage-rating/list-detail/rater.coverage.list.expanded.cells';
import { PropsOf } from '@emotion/react';
import { PropsWithChildren } from 'react';
import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
import { SubmissionType } from '@converge/graphql';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';

const SHOW_FOLD_ICON = true;

export const RaterContentTable = () => {
  const type = useRaterStateContextSelector(({ submission: { type } }) => type);

  return (
    <Table size="small">
      <RaterTableHeader />
      <TableBody>
        <RaterCoverageList />
        <RaterEndorsementList mode="primary" />
        {type === SubmissionType.Primary && (
          <RaterSubjectivitiesList mode="primary" />
        )}
      </TableBody>
    </Table>
  );
};

export const RaterTableHeader = () => {
  const showExpandedView = useRaterStateContextSelector(
    ({ showExpandedView }) => showExpandedView
  );
  const { actions, dispatch } = useRaterActionDispatchContext();

  return (
    <TableHead component="thead" className="rater-sticky-primary-table-header">
      <TableRow>
        <RaterCoverageListTableHeaderCell />
        <RaterCoverageListTableHeaderCell>
          Hazard Group
        </RaterCoverageListTableHeaderCell>
        <RaterCoverageListTableHeaderCell>
          Included
        </RaterCoverageListTableHeaderCell>
        <RaterCoverageListTableHeaderCell>
          {/* Info column */}
        </RaterCoverageListTableHeaderCell>
        <RaterCoverageListTableHeaderCell>
          Limit
        </RaterCoverageListTableHeaderCell>
        <RaterCoverageListTableHeaderCell>
          Retention
        </RaterCoverageListTableHeaderCell>
        <RaterCoverageListTableHeaderCell>
          <Stack alignItems={'center'} justifyContent={'center'}>
            <ConvergeTooltip
              title={showExpandedView ? 'Show less' : 'Show more'}
            >
              <IconButton
                onClick={() => {
                  dispatch(actions.toggleExpandedView());
                }}
                sx={{
                  margin: -2,
                }}
              >
                {showExpandedView ? (
                  <UnfoldLessIcon
                    sx={{
                      transform: 'rotate(90deg)',
                    }}
                  />
                ) : SHOW_FOLD_ICON ? (
                  <UnfoldMoreIcon
                    sx={{
                      transform: 'rotate(90deg)',
                    }}
                  />
                ) : (
                  <SettingsEthernetIcon />
                )}
              </IconButton>
            </ConvergeTooltip>
          </Stack>
        </RaterCoverageListTableHeaderCell>
        {showExpandedView && (
          <>
            <RaterCoverageListTableHeaderCell expanded>
              Limit Retention Factor
            </RaterCoverageListTableHeaderCell>
            <RaterCoverageListTableHeaderCell expanded>
              Prior Acts Factor
            </RaterCoverageListTableHeaderCell>
            <RaterCoverageListTableHeaderCell expanded>
              Waiting Period Factor
            </RaterCoverageListTableHeaderCell>
            <RaterCoverageListTableHeaderCell expanded>
              Restoration Period Factor
            </RaterCoverageListTableHeaderCell>
            <RaterCoverageListTableHeaderCell expanded>
              Enhanced Media Factor
            </RaterCoverageListTableHeaderCell>
            <RaterCoverageListTableHeaderCell expanded>
              Subcoverage Factor
            </RaterCoverageListTableHeaderCell>
            <RaterCoverageListTableHeaderCell expanded>
              Shared Coverage Aggregate Factor
            </RaterCoverageListTableHeaderCell>
            <RaterCoverageListTableHeaderCell expanded>
              Policy Term Factor
            </RaterCoverageListTableHeaderCell>
            <RaterCoverageListTableHeaderCell expanded>
              Commission Adjustment Factor
            </RaterCoverageListTableHeaderCell>
            <RaterCoverageListTableHeaderCell expanded>
              Shared Policy Aggregate Factor
            </RaterCoverageListTableHeaderCell>
          </>
        )}
        <RaterCoverageListTableHeaderCell>
          Base Premium
        </RaterCoverageListTableHeaderCell>
        <RaterCoverageListTableHeaderCell>
          Technical Premium
        </RaterCoverageListTableHeaderCell>
        <RaterCoverageListTableHeaderCell>
          Fusion Score
        </RaterCoverageListTableHeaderCell>
        <RaterCoverageListTableHeaderCell>
          Fusion Score Modifier
        </RaterCoverageListTableHeaderCell>
        <RaterCoverageListTableHeaderCell>
          Modified Premium
        </RaterCoverageListTableHeaderCell>
        <RaterCoverageListTableHeaderCell />
      </TableRow>
    </TableHead>
  );
};

const RaterCoverageListTableHeaderCell = ({
  children,
  expanded,
  sx = {},
  ...props
}: PropsOf<typeof TableCell> & { expanded?: boolean }) => {
  return (
    <TableCell
      sx={expanded ? { ...sx, ...EXPANDED_CELL_SX } : undefined}
      {...props}
    >
      {typeof children === 'string' ? (
        <Typography fontWeight={600}>{children}</Typography>
      ) : (
        children
      )}
    </TableCell>
  );
};

const StyledTypography = styled(Typography)(() => ({
  minHeight: '37px',
  display: 'flex',
  alignItems: 'center',
}));

export const RaterTableTitleCell = ({
  children,
  columnCount,
}: PropsWithChildren & { columnCount: number }) => {
  return (
    <TableCell colSpan={columnCount}>
      {typeof children === 'string' ? (
        <StyledTypography color="#ffffff" fontWeight="bold" fontSize="small">
          {children}
        </StyledTypography>
      ) : (
        children
      )}
    </TableCell>
  );
};
